// src/components/BankPosting/hooks/useBankPostingController.js
// ============================================================================
// BLOCK BP.HOOK.001 — Bank Posting controller
// ----------------------------------------------------------------------------
// PURPOSE
// - Owns page-level state for left panel, right panel, and removed-items box.
// - Keeps API concerns out of presentation components.
// ============================================================================

import React from 'react';

import {
  assignInvoicesToTransaction,
  clearRemovedItems,
  fetchBankTransactions,
  fetchPastPostings,
  fetchRemovedItems,
  fetchTransactionSuggestions,
  importBankTransactionCsvDescriptions,
  rebuildMatchSeeds,
  removeInvoicesFromTransaction,
  restoreRemovedItem,
  restoreSubmittedState,
  reversePastPosting,
  runAutoMatch,
  submitCashReceipts,
  updateAssignedInvoice,
  updateTransactionState,
} from '../api/bankPostingApi.js';
import { parseBankTransactionCsvFile } from '../csvImportUtils.js';

function toMoney(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
}

function normalizeLinkId(value) {
  return String(value ?? '').trim();
}

function normalizeTransactionKey(value) {
  return String(value ?? '').trim();
}

function trimStr(value) {
  return String(value ?? '').trim();
}

function resolveAppliedAmount(invoice) {
  const rawOverride = invoice?.applied_amount_override;

  if (rawOverride !== undefined && rawOverride !== null && String(rawOverride).trim() !== '') {
    const parsedOverride = Number(rawOverride);

    if (Number.isFinite(parsedOverride)) {
      return toMoney(parsedOverride);
    }
  }

  const rawApplied = invoice?.applied_amount;

  if (rawApplied !== undefined && rawApplied !== null && String(rawApplied).trim() !== '') {
    const parsedApplied = Number(rawApplied);

    if (Number.isFinite(parsedApplied)) {
      return toMoney(parsedApplied);
    }
  }

  // Payment-box values are the only source for the parent transaction total.
  // Auto-fill may seed the field from LOKA invoice totals, but a blank textbox
  // must never resolve to the invoice amount.
  return 0;
}

function resolveSelectedInvoiceTotal(invoice) {
  return resolveAppliedAmount(invoice);
}

function computeTransactionBalance(amountValue, selectedInvoices) {
  const amount = toMoney(amountValue);
  const invoices = Array.isArray(selectedInvoices) ? selectedInvoices : [];
  const selectedTotal = invoices.reduce(
    (sum, invoice) => sum + resolveSelectedInvoiceTotal(invoice),
    0
  );

  return toMoney(amount - selectedTotal);
}

export function applyOptimisticInvoiceDiscount(invoice, options) {
  const { normalizedLinkId, isManualOpenAssignment, nextDiscount, nextAppliedAmount } = options;
  const invoiceLinkId = normalizeLinkId(invoice?.id ?? invoice?.manual_link_id);

  if (!invoiceLinkId || invoiceLinkId !== normalizedLinkId) {
    return { invoice, changed: false };
  }

  if (Boolean(invoice?.is_manual_open_assignment) !== isManualOpenAssignment) {
    return { invoice, changed: false };
  }

  const invoiceAmount = toMoney(invoice?.invoice_amount);
  const resolvedAppliedAmount =
    nextAppliedAmount === null || nextAppliedAmount === undefined
      ? null
      : toMoney(nextAppliedAmount);

  const preserveFixedMetadata = (nextInvoice) => ({
    ...invoice,
    ...nextInvoice,
  });

  if (isManualOpenAssignment) {
    const discount = nextDiscount === null ? 0 : toMoney(nextDiscount);
    const appliedAmount =
      resolvedAppliedAmount === null ? toMoney(invoiceAmount - discount) : resolvedAppliedAmount;

    return {
      invoice: preserveFixedMetadata({
        invoice_discount: discount,
        invoice_discount_override: null,
        applied_amount: appliedAmount,
      }),
      changed: true,
    };
  }

  if (nextDiscount === null || nextDiscount === undefined) {
    const discount = toMoney(invoice?.invoice_discount);
    const fallbackAppliedAmount = toMoney(invoiceAmount - discount);

    return {
      invoice: preserveFixedMetadata({
        invoice_discount_override: null,
        applied_amount_override: resolvedAppliedAmount,
        applied_amount:
          resolvedAppliedAmount === null ? fallbackAppliedAmount : resolvedAppliedAmount,
      }),
      changed: true,
    };
  }

  const discount = toMoney(nextDiscount);
  const appliedAmount =
    resolvedAppliedAmount === null ? toMoney(invoiceAmount - discount) : resolvedAppliedAmount;

  return {
    invoice: preserveFixedMetadata({
      invoice_discount: discount,
      invoice_discount_override: discount,
      applied_amount_override: resolvedAppliedAmount,
      applied_amount: appliedAmount,
    }),
    changed: true,
  };
}

function applyOptimisticDiscountToRows(rows, options) {
  const sourceRows = Array.isArray(rows) ? rows : [];
  let changed = false;

  const nextRows = sourceRows.map((invoice) => {
    const updateResult = applyOptimisticInvoiceDiscount(invoice, options);
    if (updateResult.changed) {
      changed = true;
    }
    return updateResult.invoice;
  });

  return changed ? nextRows : sourceRows;
}

export default function useBankPostingController() {
  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const [filters, setFilters] = React.useState({
    // ------------------------------------------------------------------------
    // BLOCK BP.HOOK.001.01 — Filter state
    // ------------------------------------------------------------------------
    // IMPORTANT
    // - fromDate / toDate now belong ONLY to the Open Invoices right panel.
    // - Left bank-transaction list no longer uses these values.
    // ------------------------------------------------------------------------
    fromDate: '',
    toDate: '',
    search: '',
    removedSearch: '',
    openInvoiceSearch: '',
    suggestedSearch: '',
    pastPostingSearch: '',
  });

  // ============================================================================
  // BLOCK BP.HOOK.001.02 — Removed-item clear selection state
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Tracks which removed items are checked for clearing
  // - Checkbox selection is UI-only until user clicks Remove Cleared Items
  // ============================================================================
  const [selectedRemovedStateIds, setSelectedRemovedStateIds] = React.useState([]);

  // ============================================================================
  // BLOCK BP.HOOK.001.02A — Submit payload state
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Holds user-entered Sage submit fields that apply to the current submit batch
  // - Deposit comment override remains optional
  // ============================================================================
  const [submitForm, setSubmitForm] = React.useState({
    depositComment: '',
  });
  const [csvImporting, setCsvImporting] = React.useState(false);
  const [csvImportResult, setCsvImportResult] = React.useState(null);

  const [transactionGroups, setTransactionGroups] = React.useState([]);
  const [removedGroups, setRemovedGroups] = React.useState([]);
  const [selectedTransaction, setSelectedTransaction] = React.useState(null);
  const [autoLinkSuppressedTransactionIds, setAutoLinkSuppressedTransactionIds] = React.useState(
    []
  );
  const [selectedDetails, setSelectedDetails] = React.useState({
    transaction: null,
    suggestedInvoices: [],
    openInvoices: [],
    selectedInvoices: [],
    suggestedPagination: null,
    openPagination: null,
  });
  const [pastPostingRows, setPastPostingRows] = React.useState([]);
  const [pastPostingLoading, setPastPostingLoading] = React.useState(false);
  const [selectedPastPosting, setSelectedPastPosting] = React.useState(null);

  const resetSelectedDetails = React.useCallback(() => {
    setSelectedTransaction(null);
    setSelectedDetails({
      transaction: null,
      suggestedInvoices: [],
      openInvoices: [],
      selectedInvoices: [],
      suggestedPagination: null,
      openPagination: null,
    });
  }, []);

  const normalizeTransactionsInput = React.useCallback((input) => {
    const rows = Array.isArray(input) ? input : input ? [input] : [];
    const byId = new Map();

    rows.forEach((row) => {
      const id = normalizeTransactionKey(row?.id ?? row);
      if (!id || byId.has(id)) return;
      byId.set(id, typeof row === 'object' && row ? row : { id });
    });

    return Array.from(byId.values());
  }, []);

  const suppressAutoLinkForTransaction = React.useCallback((transactionId) => {
    const normalizedId = normalizeTransactionKey(transactionId);
    if (!normalizedId) return;

    setAutoLinkSuppressedTransactionIds((prev) =>
      prev.includes(normalizedId) ? prev : [...prev, normalizedId]
    );
  }, []);

  const loadLeftPanels = React.useCallback(async (options = {}) => {
    // Passive queue refreshes should stay read-only unless a caller explicitly
    // opts into one pre-auto-link pass.
    const preAutoLink = options?.preAutoLink === undefined ? false : Boolean(options.preAutoLink);
    const preAutoLinkLimit = Number(options?.preAutoLinkLimit);
    const normalizedPreAutoLinkLimit = Number.isFinite(preAutoLinkLimit)
      ? Math.max(Math.min(Math.trunc(preAutoLinkLimit), 500), 0)
      : null;

    setLoading(true);
    setError('');
    try {
      const [transactions, removed] = await Promise.all([
        fetchBankTransactions(
          preAutoLink
            ? normalizedPreAutoLinkLimit !== null
              ? { preAutoLink: true, preAutoLinkLimit: normalizedPreAutoLinkLimit }
              : { preAutoLink: true }
            : { preAutoLink: false }
        ),
        fetchRemovedItems(),
      ]);

      setTransactionGroups(transactions.groups);
      setRemovedGroups(removed);

      // Keep selection pinned by id without forcing callback dependency churn.
      setSelectedTransaction((prevSelected) => {
        if (!normalizeTransactionKey(prevSelected?.id)) {
          return prevSelected;
        }

        const match = transactions.groups
          .flatMap((group) => group.transactions)
          .find(
            (row) => normalizeTransactionKey(row.id) === normalizeTransactionKey(prevSelected.id)
          );

        return match || null;
      });

      return transactions;
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load Bank Posting data.');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ============================================================================
  // BLOCK BP.HOOK.001.03 — Detail query parameter builder
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Keeps open-invoice date filters scoped only to the selected transaction
  //   detail request
  // - Prevents right-panel date fields from filtering the left transaction list
  // ============================================================================
  const buildDetailParams = React.useCallback(
    (transactionOrId, pageOverrides = {}, options = {}) => {
      const transactionId = normalizeTransactionKey(transactionOrId?.id ?? transactionOrId);
      const isSuppressed =
        transactionId && autoLinkSuppressedTransactionIds.includes(transactionId);
      const forceAutoLink = Boolean(options?.forceAutoLink);

      // Detail requests are frequent and should not fall back into matching
      // work during paging/filter refreshes. Matching is now opt-in.
      const autoLinkRequested = options?.autoLink === undefined ? false : Boolean(options.autoLink);
      const autoLink = forceAutoLink ? autoLinkRequested : isSuppressed ? false : autoLinkRequested;

      return {
        ...pageOverrides,
        autoLink,
        openFromDate: filters.fromDate || undefined,
        openToDate: filters.toDate || undefined,
        openSearch: filters.openInvoiceSearch || undefined,
      };
    },
    [autoLinkSuppressedTransactionIds, filters.fromDate, filters.openInvoiceSearch, filters.toDate]
  );

  const loadTransactionDetails = React.useCallback(
    async (transaction, pageOverrides = {}, options = {}) => {
      if (!normalizeTransactionKey(transaction?.id)) return;

      setSelectedTransaction(transaction);
      setSelectedDetails({
        transaction: null,
        suggestedInvoices: [],
        openInvoices: [],
        selectedInvoices: [],
        suggestedPagination: null,
        openPagination: null,
      });
      setSubmitting(true);
      setDetailLoading(true);
      setError('');

      try {
        const details = await fetchTransactionSuggestions(
          normalizeTransactionKey(transaction.id),
          buildDetailParams(transaction, pageOverrides, options)
        );
        setSelectedDetails(details);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to load invoice suggestions.');
      } finally {
        setDetailLoading(false);
        setSubmitting(false);
      }
    },
    [buildDetailParams]
  );

  // ============================================================================
  // BLOCK BP.HOOK.001.03A — Explicit queue-side auto-match pass
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Keeps matching out of passive refresh paths
  // - Gives users one deliberate action to run the pre-auto-link queue pass
  // - Runs selected-row detail auto-link only for this explicit action
  // - Re-syncs the left tree afterward so newly linked child rows are visible
  // ============================================================================
  const handleRunAutoMatch = React.useCallback(
    async (options = {}) => {
      const transactionIds = Array.isArray(options?.transactionIds)
        ? options.transactionIds.map((value) => normalizeTransactionKey(value)).filter(Boolean)
        : [];

      setSubmitting(true);
      setError('');

      try {
        setAutoLinkSuppressedTransactionIds([]);

        const result = await runAutoMatch({
          preserveManual: true,
          startOver: false,
          transactionIds,
        });

        await Promise.all([
          loadLeftPanels({
            preAutoLink: false,
          }),

          selectedTransaction?.id
            ? loadTransactionDetails(selectedTransaction, {}, { autoLink: false })
            : Promise.resolve(),
        ]);

        return result;
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to run auto-match.');
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [loadLeftPanels, loadTransactionDetails, selectedTransaction]
  );

  const handleStartOver = React.useCallback(
    async (options = {}) => {
      const transactionIds = Array.isArray(options?.transactionIds)
        ? options.transactionIds.map((value) => normalizeTransactionKey(value)).filter(Boolean)
        : [];

      setSubmitting(true);
      setError('');

      try {
        setAutoLinkSuppressedTransactionIds([]);
        const result = await runAutoMatch({
          preserveManual: false,
          startOver: true,
          transactionIds,
        });
        await loadLeftPanels({ preAutoLink: false });

        if (selectedTransaction?.id) {
          await loadTransactionDetails(selectedTransaction, {}, { autoLink: false });
        }

        return result;
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to start over.');
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [loadLeftPanels, loadTransactionDetails, selectedTransaction]
  );

  const handleRebuildMatchSeeds = React.useCallback(async () => {
    setSubmitting(true);
    setError('');

    try {
      const result = await rebuildMatchSeeds();
      await loadLeftPanels({ preAutoLink: false });

      if (selectedTransaction?.id) {
        await loadTransactionDetails(selectedTransaction, {}, { autoLink: false });
      }

      return result;
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to rebuild match seeds.';
      setError(message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [loadLeftPanels, loadTransactionDetails, selectedTransaction]);

  const handleRestoreSubmittedState = React.useCallback(async () => {
    setSubmitting(true);
    setError('');

    try {
      const result = await restoreSubmittedState();
      await loadLeftPanels({ preAutoLink: false });

      if (selectedTransaction?.id) {
        await loadTransactionDetails(selectedTransaction, {}, { autoLink: false });
      }

      return result;
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Failed to restore submitted state.';
      setError(message);
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [loadLeftPanels, loadTransactionDetails, selectedTransaction]);

  React.useEffect(() => {
    loadLeftPanels();
  }, [loadLeftPanels]);

  // ============================================================================
  // BLOCK BP.HOOK.001.04 — Re-load selected transaction on open-date filter change
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Re-queries only the right-side Open Invoices / Suggested Invoices payload
  // - Does not re-filter the left bank transaction list
  // ============================================================================
  const openPanelFilterKey = `${filters.fromDate || ''}::${filters.toDate || ''}::${
    filters.openInvoiceSearch || ''
  }`;
  const lastOpenPanelFilterKeyRef = React.useRef(openPanelFilterKey);

  React.useEffect(() => {
    if (lastOpenPanelFilterKeyRef.current === openPanelFilterKey) return;

    lastOpenPanelFilterKeyRef.current = openPanelFilterKey;

    if (!selectedTransaction?.id) return;

    const timer = setTimeout(() => {
      loadTransactionDetails(
        selectedTransaction,
        {
          suggestedPage: 1,
          openPage: 1,
        },
        { autoLink: false }
      );
    }, 250);

    return () => clearTimeout(timer);
  }, [loadTransactionDetails, openPanelFilterKey, selectedTransaction]);

  // ============================================================================
  // BLOCK BP.HOOK.001.04A — Debounced past-postings search fetch
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Loads historical posting rows only after user enters search text
  // - Sends selected transaction amount/check as optional ranking hints
  // - Keeps list empty when search is blank to avoid unnecessary DB load
  // ============================================================================
  React.useEffect(() => {
    const search = trimStr(filters.pastPostingSearch);

    if (!search) {
      setPastPostingRows([]);
      setPastPostingLoading(false);
      return undefined;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      setPastPostingLoading(true);

      try {
        const result = await fetchPastPostings({
          search,
          limit: 250,
          transactionAmount: selectedTransaction?.amount,
          transactionCheckNo: selectedTransaction?.check_number,
        });

        if (!cancelled) {
          setPastPostingRows(Array.isArray(result?.items) ? result.items : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error ||
              err?.message ||
              'Failed to load past posting history search results.'
          );
          setPastPostingRows([]);
        }
      } finally {
        if (!cancelled) {
          setPastPostingLoading(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters.pastPostingSearch, selectedTransaction?.amount, selectedTransaction?.check_number]);

  React.useEffect(() => {
    if (!selectedPastPosting?.key) {
      return;
    }

    const keyParts = String(selectedPastPosting.key).split('|');
    if (keyParts.length !== 4) {
      setSelectedPastPosting(null);
      return;
    }

    const [depositDate, bankCode, depositNo, batchNo] = keyParts;

    const matchingRows = pastPostingRows.filter((row) => {
      const rowKey = [
        trimStr(row?.deposit_date),
        trimStr(row?.bank_code),
        trimStr(row?.deposit_no),
        trimStr(row?.batch_no),
      ].join('|');

      return rowKey === `${depositDate}|${bankCode}|${depositNo}|${batchNo}`;
    });

    if (!matchingRows.length) {
      setSelectedPastPosting(null);
      return;
    }

    const firstRow = matchingRows[0] || {};
    const nextIsReversed = matchingRows.some((row) => Boolean(row?.is_reversed));
    const nextDepositTotal = Number(firstRow?.deposit_total ?? firstRow?.posting_total ?? 0);
    const nextDepositDesc = trimStr(firstRow?.deposit_desc);
    const nextComment = trimStr(firstRow?.deposit_comment);

    setSelectedPastPosting((prev) => {
      if (!prev?.key) {
        return prev;
      }

      if (
        Boolean(prev?.isReversed) === nextIsReversed &&
        Number(prev?.depositTotal || 0) === nextDepositTotal &&
        trimStr(prev?.depositDesc) === nextDepositDesc &&
        trimStr(prev?.depositComment) === nextComment
      ) {
        return prev;
      }

      return {
        ...prev,
        isReversed: nextIsReversed,
        depositTotal: nextDepositTotal,
        depositDesc: nextDepositDesc,
        depositComment: nextComment,
      };
    });
  }, [pastPostingRows, selectedPastPosting]);

  const setFilterValue = React.useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setSubmitValue = React.useCallback((key, value) => {
    setSubmitForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSelectPastPosting = React.useCallback((posting) => {
    if (!posting || !posting.key) {
      setSelectedPastPosting(null);
      return;
    }

    setSelectedPastPosting(posting);
  }, []);

  // ============================================================================
  // BLOCK BP.HOOK.001.05 — Assign invoices to selected transaction
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Persists checked Suggested/Open invoice rows to the selected bank transaction
  // - Rejects silent no-op responses where backend returns 200 but adds zero links
  // ============================================================================
  const handleAssignInvoices = React.useCallback(
    async (rows) => {
      if (!normalizeTransactionKey(selectedTransaction?.id) || !rows?.length) return;

      setSubmitting(true);
      setError('');

      try {
        const result = await assignInvoicesToTransaction(
          normalizeTransactionKey(selectedTransaction.id),
          rows
        );

        if (Number(result?.added_count ?? 0) <= 0) {
          throw new Error(
            result?.unresolved_rows?.length
              ? `No invoice links were created. First unresolved row: ${JSON.stringify(result.unresolved_rows[0])}`
              : 'No invoice links were created. The selected rows could not be matched to LOKA.invoice.'
          );
        }

        suppressAutoLinkForTransaction(selectedTransaction.id);

        await Promise.all([
          loadTransactionDetails(selectedTransaction, {}, { autoLink: false }),
          loadLeftPanels({ preAutoLink: false }),
        ]);
      } catch (err) {
        setError(
          err?.response?.data?.error || err?.message || 'Failed to assign selected invoices.'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [loadLeftPanels, loadTransactionDetails, selectedTransaction, suppressAutoLinkForTransaction]
  );

  const handleRemoveInvoices = React.useCallback(
    async (invoiceKeys, options = {}) => {
      const targetTransactionId = normalizeTransactionKey(
        options?.transactionId ?? selectedTransaction?.id
      );

      if (!targetTransactionId || !invoiceKeys?.length) {
        return;
      }

      setSubmitting(true);
      setError('');

      try {
        await removeInvoicesFromTransaction(targetTransactionId, invoiceKeys);
        suppressAutoLinkForTransaction(targetTransactionId);

        const reloadTasks = [loadLeftPanels({ preAutoLink: false })];
        if (normalizeTransactionKey(selectedTransaction?.id) === targetTransactionId) {
          reloadTasks.push(loadTransactionDetails(selectedTransaction, {}, { autoLink: false }));
        }

        await Promise.all(reloadTasks);
      } catch (err) {
        setError(
          err?.response?.data?.error || err?.message || 'Failed to remove selected invoices.'
        );
      } finally {
        setSubmitting(false);
      }
    },
    [loadLeftPanels, loadTransactionDetails, selectedTransaction, suppressAutoLinkForTransaction]
  );

  const handleMarkRemoved = React.useCallback(
    async (transactionOrTransactions) => {
      const transactions = normalizeTransactionsInput(transactionOrTransactions);
      if (!transactions.length) return;

      setSubmitting(true);
      setError('');
      try {
        await Promise.all(
          transactions.map((transaction) =>
            updateTransactionState(normalizeTransactionKey(transaction.id), {
              state_code: 'removed',
              is_removed: true,
              is_cleared: false,
            })
          )
        );

        const removedIds = new Set(transactions.map((row) => normalizeTransactionKey(row.id)));
        if (
          selectedTransaction?.id &&
          removedIds.has(normalizeTransactionKey(selectedTransaction.id))
        ) {
          resetSelectedDetails();
        }

        await loadLeftPanels({ preAutoLink: false });
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to remove selected rows.');
      } finally {
        setSubmitting(false);
      }
    },
    [loadLeftPanels, normalizeTransactionsInput, resetSelectedDetails, selectedTransaction?.id]
  );

  const handleSubmitSelected = React.useCallback(
    async (transactionOrTransactions) => {
      const transactions = normalizeTransactionsInput(transactionOrTransactions);
      if (!transactions.length) return;

      setSubmitting(true);
      setError('');
      try {
        await submitCashReceipts({
          transaction_ids: transactions.map((row) => normalizeTransactionKey(row.id)),
          deposit_comment_override: submitForm.depositComment,
          deposit_comment: submitForm.depositComment,
        });

        const submittedIds = new Set(transactions.map((row) => normalizeTransactionKey(row.id)));
        if (
          selectedTransaction?.id &&
          submittedIds.has(normalizeTransactionKey(selectedTransaction.id))
        ) {
          resetSelectedDetails();
        }

        await loadLeftPanels({ preAutoLink: false });
      } catch (err) {
        setError(err?.response?.data?.error || err?.message || 'Failed to submit selected rows.');
      } finally {
        setSubmitting(false);
      }
    },
    [
      loadLeftPanels,
      normalizeTransactionsInput,
      resetSelectedDetails,
      selectedTransaction?.id,
      submitForm.depositComment,
    ]
  );

  const handleReversePastPosting = React.useCallback(async () => {
    if (!selectedPastPosting?.key) {
      return;
    }

    const keyParts = String(selectedPastPosting.key).split('|');
    if (keyParts.length !== 4) {
      setError('Selected past posting key is invalid. Please reselect the posting and retry.');
      throw new Error(
        'Selected past posting key is invalid. Please reselect the posting and retry.'
      );
    }

    const [depositDate, bankCode, depositNo, batchNo] = keyParts;

    setSubmitting(true);
    setError('');

    try {
      const result = await reversePastPosting({
        deposit_date: depositDate,
        bank_code: bankCode,
        deposit_no: depositNo,
        batch_no: batchNo,
        deposit_comment_override: submitForm.depositComment,
      });

      await loadLeftPanels({ preAutoLink: false });

      const search = trimStr(filters.pastPostingSearch);
      if (search) {
        const refreshedPastPostings = await fetchPastPostings({
          search,
          limit: 250,
          transactionAmount: selectedTransaction?.amount,
          transactionCheckNo: selectedTransaction?.check_number,
        });
        setPastPostingRows(
          Array.isArray(refreshedPastPostings?.items) ? refreshedPastPostings.items : []
        );
      }

      return result;
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to reverse past posting.');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }, [
    filters.pastPostingSearch,
    loadLeftPanels,
    selectedPastPosting,
    selectedTransaction?.amount,
    selectedTransaction?.check_number,
    submitForm.depositComment,
  ]);

  const handleRestoreRemoved = React.useCallback(
    async (stateIdOrIds) => {
      const explicitIds = Array.isArray(stateIdOrIds)
        ? stateIdOrIds.map(Number).filter(Number.isFinite)
        : Number.isFinite(Number(stateIdOrIds))
          ? [Number(stateIdOrIds)]
          : [];

      const idsToRestore = explicitIds.length ? explicitIds : selectedRemovedStateIds;

      if (!idsToRestore.length) return;

      setSubmitting(true);
      try {
        await Promise.all(idsToRestore.map((id) => restoreRemovedItem(id)));
        setSelectedRemovedStateIds((prev) => prev.filter((id) => !idsToRestore.includes(id)));
        await loadLeftPanels({ preAutoLink: false });
      } finally {
        setSubmitting(false);
      }
    },
    [loadLeftPanels, selectedRemovedStateIds]
  );

  // ============================================================================
  // BLOCK BP.HOOK.001.06 — Toggle removed-item clear selection
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Checkbox only marks/unmarks removed items for clearing
  // - Does not persist cleared state until Remove Cleared Items is clicked
  // ============================================================================
  const updateRemovedClearSelection = React.useCallback((stateIds, checked) => {
    const normalizedIds = Array.from(
      new Set(
        (Array.isArray(stateIds) ? stateIds : []).map((id) => Number(id)).filter(Number.isFinite)
      )
    );

    if (!normalizedIds.length) return;

    setSelectedRemovedStateIds((prev) => {
      if (checked) {
        const merged = new Set(prev);
        normalizedIds.forEach((id) => merged.add(id));
        return Array.from(merged);
      }

      const removeSet = new Set(normalizedIds);
      return prev.filter((rowId) => !removeSet.has(rowId));
    });
  }, []);

  const handleUpdateRemovedClearFlag = React.useCallback(
    (item, checked) => {
      updateRemovedClearSelection([item?.id], checked);
    },
    [updateRemovedClearSelection]
  );

  const handleToggleAllRemovedClearFlags = React.useCallback(
    (stateIds, checked) => {
      updateRemovedClearSelection(stateIds, checked);
    },
    [updateRemovedClearSelection]
  );

  // ============================================================================
  // BLOCK BP.HOOK.001.07 — Persist cleared removed items
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Writes cleared state only after user clicks Remove Cleared Items
  // ============================================================================
  const handleClearRemovedItems = React.useCallback(async () => {
    if (!selectedRemovedStateIds.length) return;

    setSubmitting(true);
    try {
      await clearRemovedItems(selectedRemovedStateIds);
      setSelectedRemovedStateIds([]);
      await loadLeftPanels({ preAutoLink: false });
    } finally {
      setSubmitting(false);
    }
  }, [loadLeftPanels, selectedRemovedStateIds]);

  // ============================================================================
  // BLOCK BP.HOOK.001.07A — CSV description import flow
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Parses a local CSV file in-browser and posts normalized rows to backend
  // - Persists only safe matches (backend-enforced) and refreshes left panels
  // - Exposes import summary for toast/dialog presentation
  // ============================================================================
  const handleImportCsvDescriptions = React.useCallback(
    async (file, options = {}) => {
      if (!file) {
        throw new Error('Choose a CSV file first.');
      }

      setCsvImporting(true);
      setError('');

      try {
        const rows = await parseBankTransactionCsvFile(file);
        const rebuildSeedsRequested = Boolean(options?.rebuildMatchSeeds);
        const runAutoMatchRequested = Boolean(options?.runAutoMatch);

        if (!rows.length) {
          throw new Error(
            'No usable CSV rows found. Verify the file has Post Date, Amount, and Description/Text columns.'
          );
        }

        const result = await importBankTransactionCsvDescriptions(rows);
        let rebuildSummary = null;
        let autoMatchSummary = null;

        if (rebuildSeedsRequested) {
          const rebuildResult = await rebuildMatchSeeds();
          rebuildSummary = rebuildResult?.summary || null;
        }

        if (runAutoMatchRequested) {
          setAutoLinkSuppressedTransactionIds([]);
          const autoMatchResult = await runAutoMatch({
            preserveManual: true,
            startOver: false,
            limit: 500,
          });
          autoMatchSummary = autoMatchResult?.summary || null;
        }

        setCsvImportResult({
          ...result,
          fileName: file.name,
          postActions: {
            rebuiltMatchSeeds: rebuildSeedsRequested,
            rebuildSummary,
            ranAutoMatch: runAutoMatchRequested,
            autoMatchSummary,
          },
        });

        await loadLeftPanels({ preAutoLink: false });

        if (selectedTransaction?.id) {
          await loadTransactionDetails(selectedTransaction, {}, { autoLink: false });
        }

        return {
          ...result,
          postActions: {
            rebuiltMatchSeeds: rebuildSeedsRequested,
            rebuildSummary,
            ranAutoMatch: runAutoMatchRequested,
            autoMatchSummary,
          },
        };
      } catch (err) {
        const message = err?.response?.data?.error || err?.message || 'Failed to import bank CSV.';
        setError(message);
        throw err;
      } finally {
        setCsvImporting(false);
      }
    },
    [loadLeftPanels, loadTransactionDetails, selectedTransaction]
  );

  const clearCsvImportResult = React.useCallback(() => {
    setCsvImportResult(null);
  }, []);

  // ============================================================================
  // BLOCK BP.HOOK.001.08 — Update assigned invoice row
  // ----------------------------------------------------------------------------
  // PURPOSE
  // - Saves editable payment/discount values for selected invoice lines
  // - Reloads details + transaction list so remaining balance stays in sync
  // ============================================================================
  const handleUpdateAssignedInvoice = React.useCallback(
    async (transactionId, linkId, payload) => {
      const targetTransactionId = normalizeTransactionKey(transactionId);
      const normalizedLinkId = normalizeLinkId(linkId);

      if (!targetTransactionId || !normalizedLinkId) return;

      const rawDiscount = payload?.invoice_discount;
      const nextDiscount =
        rawDiscount === '' || rawDiscount === null || rawDiscount === undefined
          ? null
          : toMoney(rawDiscount);

      const rawAppliedAmount = payload?.applied_amount;
      const nextAppliedAmount =
        rawAppliedAmount === '' || rawAppliedAmount === null || rawAppliedAmount === undefined
          ? null
          : toMoney(rawAppliedAmount);

      const isManualOpenAssignment = Boolean(payload?.is_manual_open_assignment);
      const requestPayload = {
        ...payload,
        invoice_discount: nextDiscount,
        applied_amount: nextAppliedAmount,
      };

      const optimisticOptions = {
        normalizedLinkId,
        isManualOpenAssignment,
        nextDiscount,
        nextAppliedAmount,
      };

      const selectedId = normalizeTransactionKey(selectedTransaction?.id);
      const selectedIsTarget = Boolean(selectedId) && selectedId === targetTransactionId;

      const optimisticSelectedInvoices = selectedIsTarget
        ? applyOptimisticDiscountToRows(selectedDetails?.selectedInvoices, optimisticOptions)
        : null;

      if (selectedIsTarget && optimisticSelectedInvoices !== selectedDetails?.selectedInvoices) {
        setSelectedDetails((prev) => {
          if (normalizeTransactionKey(prev?.transaction?.id) !== targetTransactionId) {
            return prev;
          }

          return {
            ...prev,
            selectedInvoices: optimisticSelectedInvoices,
          };
        });

        setSelectedTransaction((prev) => {
          if (!prev || normalizeTransactionKey(prev.id) !== targetTransactionId) {
            return prev;
          }

          return {
            ...prev,
            balance: computeTransactionBalance(prev.amount, optimisticSelectedInvoices),
          };
        });
      }

      setTransactionGroups((prevGroups) =>
        prevGroups.map((group) => {
          const sourceTransactions = Array.isArray(group?.transactions) ? group.transactions : [];

          return {
            ...group,
            transactions: sourceTransactions.map((row) => {
              if (normalizeTransactionKey(row?.id) !== targetTransactionId) {
                return row;
              }

              // IMPORTANT:
              // Update the child objects that already belong to the transaction tree.
              // Do NOT replace them with selectedDetails.selectedInvoices because the
              // tree rows can contain additional display metadata.
              const treeSelectedInvoices = Array.isArray(row?.selected_invoices)
                ? row.selected_invoices
                : [];

              const nextSelectedInvoices = applyOptimisticDiscountToRows(
                treeSelectedInvoices,
                optimisticOptions
              );

              if (nextSelectedInvoices === row?.selected_invoices) {
                return row;
              }

              return {
                ...row,
                selected_invoices: nextSelectedInvoices,
                balance: computeTransactionBalance(row?.amount, nextSelectedInvoices),
              };
            }),
          };
        })
      );

      setSubmitting(true);
      setError('');
      try {
        suppressAutoLinkForTransaction(targetTransactionId);

        // Only persistence is needed after a successful inline child edit.
        // The UI was already updated optimistically above and should stay local
        // until the server save fails.
        await updateAssignedInvoice(targetTransactionId, normalizedLinkId, requestPayload);
      } catch (err) {
        const message =
          err?.response?.data?.error || err?.message || 'Failed to update assigned invoice.';

        // eslint-disable-next-line no-console
        console.error('[BankPosting] inline invoice save failed', {
          transactionId: targetTransactionId,
          linkId: normalizedLinkId,
          status: err?.response?.status,
          response: err?.response?.data,
          payload: requestPayload,
        });

        setError(message);

        // IMPORTANT:
        // Do not reload the page state after a failed inline edit.
        // Keep the user's entered Payment/Discount values visible.
        throw err;
      } finally {
        setSubmitting(false);
      }
    },
    [selectedDetails?.selectedInvoices, selectedTransaction, suppressAutoLinkForTransaction]
  );

  const handleChangeSuggestionPage = React.useCallback(
    async (page) => {
      if (!selectedTransaction?.id) return;
      await loadTransactionDetails(
        selectedTransaction,
        {
          suggestedPage: page,
          openPage: selectedDetails?.openPagination?.page || 1,
        },
        { autoLink: false }
      );
    },
    [loadTransactionDetails, selectedDetails?.openPagination?.page, selectedTransaction]
  );

  const handleChangeOpenPage = React.useCallback(
    async (page) => {
      if (!selectedTransaction?.id) return;
      await loadTransactionDetails(
        selectedTransaction,
        {
          suggestedPage: selectedDetails?.suggestedPagination?.page || 1,
          openPage: page,
        },
        { autoLink: false }
      );
    },
    [loadTransactionDetails, selectedDetails?.suggestedPagination?.page, selectedTransaction]
  );

  return {
    loading,
    submitting,
    detailLoading,
    error,
    filters,
    transactionGroups,
    removedGroups,
    selectedTransaction,
    selectedDetails,
    pastPostingRows,
    pastPostingLoading,
    selectedPastPosting,
    selectedRemovedStateIds,
    submitForm,
    csvImporting,
    csvImportResult,
    setFilterValue,
    setSubmitValue,
    clearCsvImportResult,
    handleSelectPastPosting,
    loadLeftPanels,
    loadTransactionDetails,
    handleRunAutoMatch,
    handleStartOver,
    handleRebuildMatchSeeds,
    handleRestoreSubmittedState,
    handleAssignInvoices,
    handleRemoveInvoices,
    handleMarkRemoved,
    handleSubmitSelected,
    handleReversePastPosting,
    handleRestoreRemoved,
    handleUpdateRemovedClearFlag,
    handleToggleAllRemovedClearFlags,
    handleClearRemovedItems,
    handleImportCsvDescriptions,
    handleUpdateAssignedInvoice,
    handleChangeSuggestionPage,
    handleChangeOpenPage,
  };
}
